import React, { useEffect, useRef } from 'react';

const BRELLA_SCRIPT_URL = 'https://widget.brella.io';

// Brella's loader mounts on a hardcoded `#brella-widget` selector and reads config from
// a `<script type="text/props">` that must be a *child* of the mount node. Neither is
// configurable.
//
// The mount must have no React children: the loader renders with `clean: true`, wiping
// `innerHTML` before it mounts. It is a block-display <span> rather than a <div> because
// the authored marker sits inside a <p>, where a <div> is invalid nesting; Brella's
// selector is tag-agnostic.
const BRELLA_MOUNT_ID = 'brella-widget';

// Set once for all events. The marker carries only type and join code, so that it stays
// short enough to type by hand; everything else lives here.
const DEFAULT_PROPS: Record<string, Record<string, unknown>> = {
  speakers: {
    hideFeaturedSpeakersFromAllSpeakers: true,
    labels: {
      featuredSpeakers: 'Featured Speakers',
      allSpeakers: 'All speakers',
      learnMore: 'Learn more',
    },
    paginationPageSize: 12,
  },
  'schedule-tracks': {
    layout: 'simplified',
    accentColor: 'rose',
    colorTheme: 'monochrome',
    fixedHeight: true,
  },
};

declare global {
  interface Window {
    brellaWidgetRender?: () => void;
  }
}

const MOUNT_STYLE: Record<string, React.CSSProperties> = {
  speakers: {
    '--brella-color-primary-600': '#f32a52',
    '--brella-widget-speaker-gradient-rgb': '47, 53, 56',
  } as React.CSSProperties,
  'schedule-tracks': { height: '600px' },
};
const BASE_MOUNT_STYLE: React.CSSProperties = { display: 'block' };

export interface BrellaWidgetProps {
  widgetType: string;
  joinCode: string;
}

const BrellaWidget = ({ widgetType, joinCode }: BrellaWidgetProps) => {
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const mount = ref.current;
    if (!mount) return;

    const props = { joinCode, widgetType, ...(DEFAULT_PROPS[widgetType] || {}) };
    const propsTag = document.createElement('script');
    propsTag.type = 'text/props';
    propsTag.textContent = JSON.stringify(props);
    mount.appendChild(propsTag);

    // Fetch the loader once per page. If it is already present it has already
    // auto-rendered, so re-trigger it for this newly mounted node instead.
    const existing = document.querySelector(`script[src="${BRELLA_SCRIPT_URL}"]`);
    if (existing) {
      window.brellaWidgetRender?.();
    } else {
      const loader = document.createElement('script');
      loader.async = true;
      loader.src = BRELLA_SCRIPT_URL;
      document.body.appendChild(loader);
    }
  }, [widgetType, joinCode]);

  return (
    <span
      id={BRELLA_MOUNT_ID}
      ref={ref}
      style={{ ...BASE_MOUNT_STYLE, ...(MOUNT_STYLE[widgetType] || {}) }}
    />
  );
};

export default BrellaWidget;
